import React from 'react';

/**
 * EmptyState — shown when a section has no data.
 *
 * Props:
 *   icon        — SVG element (will be rendered at 40x40 or 24x24 depending on size)
 *   title       — bold heading
 *   description — muted body text
 *   ctaLabel    — optional button label
 *   onCta       — optional button click handler
 *   size        — 'large' (default) | 'small'
 *   accent      — optional CSS color for title + icon container (default: var(--text3))
 *                 pass 'var(--green)' for success states
 */
export default function EmptyState({ icon, title, description, ctaLabel, onCta, size = 'large', accent }) {
  const isLarge = size === 'large';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: isLarge ? 280 : 120,
        padding: isLarge ? '40px 20px' : '20px 16px',
        textAlign: 'center',
      }}
    >
      {/* Icon container */}
      <div
        style={{
          width: isLarge ? 80 : 44,
          height: isLarge ? 80 : 44,
          borderRadius: isLarge ? 20 : 12,
          background: 'var(--bg3, var(--surface))',
          border: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: isLarge ? 20 : 12,
          color: accent || 'var(--text3)',
          flexShrink: 0,
        }}
      >
        {/* Scale the icon */}
        <div style={{ width: isLarge ? 40 : 24, height: isLarge ? 40 : 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </div>
      </div>

      {/* Title */}
      <div
        style={{
          fontSize: isLarge ? 16 : 13,
          fontWeight: 700,
          color: accent || 'var(--text)',
          marginBottom: isLarge ? 8 : 6,
          lineHeight: 1.3,
        }}
      >
        {title}
      </div>

      {/* Description */}
      {description && (
        <div
          style={{
            fontSize: isLarge ? 14 : 12,
            color: 'var(--text3)',
            lineHeight: 1.6,
            maxWidth: isLarge ? 320 : 240,
            marginBottom: ctaLabel ? (isLarge ? 24 : 16) : 0,
          }}
        >
          {description}
        </div>
      )}

      {/* CTA button */}
      {ctaLabel && onCta && (
        <button
          onClick={onCta}
          style={{
            fontSize: isLarge ? 13 : 12,
            fontWeight: 600,
            padding: isLarge ? '10px 20px' : '7px 16px',
            borderRadius: 8,
            cursor: 'pointer',
            border: '1px solid var(--accent)',
            background: 'var(--accent)',
            color: '#fff',
            fontFamily: 'var(--font)',
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
}
